export interface CookieConsentCategory {
  id: string;
  label: string;
  description?: string;
  required?: boolean;
}

export interface CookieConsentSettings {
  banner_enabled?: boolean;
  banner_text?: string;
  accept_all_label?: string;
  reject_non_essential_label?: string;
  customize_label?: string;
  banner_position?: 'bottom' | 'modal';
  primary_color?: string;
  policy_url?: string;
  categories?: CookieConsentCategory[];
  /** One path per line or comma-separated. If set, banner only shows on these paths (prefix match). Empty = all pages. */
  enabled_paths?: string;
  /** One path per line or comma-separated. Banner never shows on these paths (prefix or exact match). */
  excluded_paths?: string;
}

export interface CookieConsentConfig {
  success: boolean;
  settings: CookieConsentSettings;
  cookies?: Array<{ id: string; name: string; domain?: string; category: string; description?: string; is_required: boolean }>;
}

export interface CookieConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

export const CONSENT_STORAGE_KEY_PREFIX = 'cookie_consent_';

export function getConsentStorageKey(brandId: string): string {
  return `${CONSENT_STORAGE_KEY_PREFIX}${brandId}`;
}

export function getStoredConsent(brandId: string): CookieConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(getConsentStorageKey(brandId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentState;
    return parsed && typeof parsed.timestamp === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

const CONSENT_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

export function setStoredConsent(brandId: string, state: CookieConsentState): void {
  if (typeof window === 'undefined') return;
  const payload = { ...state, timestamp: new Date().toISOString() };
  try {
    localStorage.setItem(getConsentStorageKey(brandId), JSON.stringify(payload));
    // Also set a cookie so the backend can read consent (e.g. for server-side script loading)
    const cookieName = getConsentCookieName(brandId);
    const value = encodeURIComponent(JSON.stringify(payload));
    document.cookie = `${cookieName}=${value}; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    // ignore
  }
}

export function getConsentCookieName(brandId: string): string {
  return `cc_${brandId.replace(/-/g, '_')}`;
}

/**
 * Parse paths string (newline or comma separated) into trimmed non-empty list.
 */
export function parsePathsSetting(value: string | undefined): string[] {
  if (!value || typeof value !== 'string') return [];
  return value
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Return true if the banner should show on this path given enabled_paths and excluded_paths.
 * - If enabled_paths has entries: path must match at least one (prefix or exact).
 * - If excluded_paths has entries: path must not match any (prefix or exact).
 */
export function isPathAllowed(pathname: string, enabled_paths: string[], excluded_paths: string[]): boolean {
  const path = pathname || '/';
  for (const ex of excluded_paths) {
    if (path === ex || path.startsWith(ex + '/')) return false;
  }
  if (enabled_paths.length === 0) return true;
  for (const en of enabled_paths) {
    if (path === en || path.startsWith(en + '/')) return true;
  }
  return false;
}
